import React, { useEffect, useRef } from 'react';

// ── Simplex 3D Noise (ported from WaterDash utils.js) ──────────────────────

const F3 = 1 / 3;
const G3 = 1 / 6;

const grad3 = [
  [1,1,0],[-1,1,0],[1,-1,0],[-1,-1,0],
  [1,0,1],[-1,0,1],[1,0,-1],[-1,0,-1],
  [0,1,1],[0,-1,1],[0,1,-1],[0,-1,-1],
];

const perm = new Uint8Array(512);
const gradP: number[][] = new Array(512);

(function seed(s: number) {
  const p = new Uint8Array(256);
  if (s > 0 && s < 1) s *= 65536;
  s = Math.floor(s);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    s = (s * 16807 + 0) % 2147483647;
    const n = (s % (i + 1) + i + 1) % (i + 1);
    const tmp = p[i];
    p[i] = p[n];
    p[n] = tmp;
  }
  for (let i = 0; i < 512; i++) {
    perm[i] = p[i & 255];
    gradP[i] = grad3[perm[i] % 12];
  }
})(42);

function dot3(g: number[], x: number, y: number, z: number) {
  return g[0] * x + g[1] * y + g[2] * z;
}

function simplex3(x: number, y: number, z: number): number {
  const s = (x + y + z) * F3;
  const i = Math.floor(x + s);
  const j = Math.floor(y + s);
  const k = Math.floor(z + s);
  const t = (i + j + k) * G3;
  const x0 = x - (i - t);
  const y0 = y - (j - t);
  const z0 = z - (k - t);

  let i1: number, j1: number, k1: number, i2: number, j2: number, k2: number;
  if (x0 >= y0) {
    if (y0 >= z0)      { i1=1;j1=0;k1=0;i2=1;j2=1;k2=0; }
    else if (x0 >= z0) { i1=1;j1=0;k1=0;i2=1;j2=0;k2=1; }
    else               { i1=0;j1=0;k1=1;i2=1;j2=0;k2=1; }
  } else {
    if (y0 < z0)       { i1=0;j1=0;k1=1;i2=0;j2=1;k2=1; }
    else if (x0 < z0)  { i1=0;j1=1;k1=0;i2=0;j2=1;k2=1; }
    else               { i1=0;j1=1;k1=0;i2=1;j2=1;k2=0; }
  }

  const x1 = x0 - i1 + G3, y1 = y0 - j1 + G3, z1 = z0 - k1 + G3;
  const x2 = x0 - i2 + 2 * G3, y2 = y0 - j2 + 2 * G3, z2 = z0 - k2 + 2 * G3;
  const x3 = x0 - 1 + 3 * G3, y3 = y0 - 1 + 3 * G3, z3 = z0 - 1 + 3 * G3;

  const ii = i & 255, jj = j & 255, kk = k & 255;
  const gi0 = gradP[ii + perm[jj + perm[kk]]];
  const gi1 = gradP[ii + i1 + perm[jj + j1 + perm[kk + k1]]];
  const gi2 = gradP[ii + i2 + perm[jj + j2 + perm[kk + k2]]];
  const gi3 = gradP[ii + 1 + perm[jj + 1 + perm[kk + 1]]];

  let n0: number, n1: number, n2: number, n3: number;
  let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;
  n0 = t0 < 0 ? 0 : (t0 *= t0, t0 * t0 * dot3(gi0, x0, y0, z0));
  let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;
  n1 = t1 < 0 ? 0 : (t1 *= t1, t1 * t1 * dot3(gi1, x1, y1, z1));
  let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;
  n2 = t2 < 0 ? 0 : (t2 *= t2, t2 * t2 * dot3(gi2, x2, y2, z2));
  let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;
  n3 = t3 < 0 ? 0 : (t3 *= t3, t3 * t3 * dot3(gi3, x3, y3, z3));

  return 32 * (n0 + n1 + n2 + n3);
}

// ── Particle type ──────────────────────────────────────────────────────────

interface Particle {
  x: number;
  y: number;
  size: number;
  speed: number;
  drift: number;
  driftSpeed: number;
  opacity: number;
  hue: number;
  sat: number;
}

function createParticle(w: number, h: number, randomY: boolean): Particle {
  const isMagenta = Math.random() < 0.2;
  return {
    x: Math.random() * w,
    y: randomY ? Math.random() * h : h + Math.random() * 50,
    size: 0.8 + Math.random() * 2.2,
    speed: 0.15 + Math.random() * 0.4,
    drift: Math.random() * Math.PI * 2,
    driftSpeed: 0.0005 + Math.random() * 0.001,
    opacity: 0.15 + Math.random() * 0.5,
    hue: isMagenta ? 300 + Math.random() * 20 : 170 + Math.random() * 15,
    sat: isMagenta ? 100 : 90 + Math.random() * 10,
  };
}

// ── Component ──────────────────────────────────────────────────────────────

const OceanBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobileMode = window.innerWidth <= 700;
    const particleCount = mobileMode ? 80 : 250;

    // Offscreen caustic canvas
    const causticCanvas = document.createElement('canvas');
    const causticCtx = causticCanvas.getContext('2d')!;

    let width = window.innerWidth;
    let height = window.innerHeight;
    let causticW: number, causticH: number;
    let frameCount = 0;
    let animId: number;

    const particles: Particle[] = [];

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas!.width = width;
      canvas!.height = height;

      const scale = mobileMode ? 6 : 4;
      causticW = Math.ceil(width / scale);
      causticH = Math.ceil(height / scale);
      causticCanvas.width = causticW;
      causticCanvas.height = causticH;
    }

    function initParticles() {
      for (let i = 0; i < particleCount; i++) {
        particles.push(createParticle(width, height, true));
      }
    }

    function renderCaustics(time: number) {
      const imageData = causticCtx.createImageData(causticW, causticH);
      const data = imageData.data;

      for (let y = 0; y < causticH; y++) {
        for (let x = 0; x < causticW; x++) {
          const n = simplex3(x * 0.012, y * 0.012, time * 0.00012);
          const n2 = simplex3(x * 0.006 + 100, y * 0.006 + 100, time * 0.00008);
          const combined = (n + n2 * 0.5) / 1.5;
          const brightness = Math.pow(Math.max(0, combined), 3) * 55;

          const i = (y * causticW + x) * 4;
          data[i] = brightness * 0.15;
          data[i + 1] = brightness * 0.85;
          data[i + 2] = brightness * 0.7;
          data[i + 3] = brightness * 3;
        }
      }

      causticCtx.putImageData(imageData, 0, 0);
    }

    function renderParticles() {
      const tealParticles: Particle[] = [];
      const magentaParticles: Particle[] = [];

      for (const p of particles) {
        p.y -= p.speed;
        p.drift += p.driftSpeed;
        p.x += Math.sin(p.drift) * 0.3;

        if (p.y < -10) {
          Object.assign(p, createParticle(width, height, false));
        }

        if (p.hue < 200) {
          tealParticles.push(p);
        } else {
          magentaParticles.push(p);
        }
      }

      ctx!.shadowBlur = mobileMode ? 5 : 8;
      ctx!.shadowColor = 'rgba(0, 255, 200, 0.5)';
      for (const p of tealParticles) {
        ctx!.globalAlpha = p.opacity;
        ctx!.fillStyle = `hsl(${p.hue}, ${p.sat}%, 60%)`;
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx!.fill();
      }

      ctx!.shadowColor = 'rgba(255, 0, 180, 0.5)';
      for (const p of magentaParticles) {
        ctx!.globalAlpha = p.opacity;
        ctx!.fillStyle = `hsl(${p.hue}, ${p.sat}%, 55%)`;
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx!.fill();
      }

      ctx!.shadowBlur = 0;
      ctx!.globalAlpha = 1;
    }

    function render(time: number) {
      frameCount++;

      ctx!.fillStyle = '#000a14';
      ctx!.fillRect(0, 0, width, height);

      if (reducedMotion) {
        animId = requestAnimationFrame(render);
        return;
      }

      // Throttle caustics on mobile
      if (!mobileMode || frameCount % 2 === 0) {
        renderCaustics(time);
      }
      ctx!.globalAlpha = 0.14;
      ctx!.drawImage(causticCanvas, 0, 0, width, height);
      ctx!.globalAlpha = 1;

      renderParticles();

      animId = requestAnimationFrame(render);
    }

    resize();
    initParticles();
    window.addEventListener('resize', resize);
    animId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default OceanBackground;
